import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { UserInteractionService } from '@usheru-hff/community-elements/utils';
import { List } from '@usheru-hff/shared/domain';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { map, throwError } from 'rxjs';
import { UiMandatoryMessageComponent } from '@usheru-hff/authentication/ui-mandatory-message';

@Component({
  selector: 'community-elements-ui-create-list-form',
  template: `
    <div class="title" i18n="@@ui-create-list-form.title">Create a new List</div>
    <form [formGroup]="createListForm" class="form-group">
      <div class="form-field">
        <label for="name" class="form-field__mandatory" i18n="@@ui-create-list-form.form-field.name">Name</label>
        <input pInputText type="text" class="form-control" formControlName="name" />
      </div>
      <div class="form-field">
        <label for="description" i18n="@@ui-create-list-form.form-field.description">Description</label>
        <textarea pInputTextarea autoResize="true" class="form-control" formControlName="description"></textarea>
      </div>
      <div class="form-field">
        <label for="visibility" class="form-field__mandatory" i18n="@@ui-create-list-form.form-field.visibility"
          >Visibility</label
        >
        <p-dropdown
          formControlName="visibility"
          [options]="visibilityOptions"
          optionLabel="name"
          optionValue="value"
        ></p-dropdown>
      </div>
      <ui-mandatory-message></ui-mandatory-message>
      <button
        pButton
        type="submit"
        class="btn btn-primary"
        [loading]="waitingForResponse"
        [disabled]="!createListForm.valid"
        (click)="onCreateListClick()"
        label="Create"
        i18n-label="@@ui-create-list-form.create-btn"
      ></button>
    </form>
  `,
  styleUrls: ['./ui-create-list-form.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DropdownModule,
    ButtonModule,
    InputTextareaModule,
    InputTextModule,
    UiMandatoryMessageComponent
  ]
  // changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiCreateListFormComponent implements OnInit {
  @Output() listCreated = new EventEmitter<List>();

  createListForm: FormGroup;
  waitingForResponse = false;

  visibilityOptions = [
    { name: $localize`:@@public.commons.public:Public`, value: true },
    { name: $localize`:@@public.commons.private:Private`, value: false }
  ];

  constructor(
    private userInteractionService: UserInteractionService,
    private messageService: MessageService,
    private formBuilder: FormBuilder,
    private cd: ChangeDetectorRef
  ) {}
  ngOnInit(): void {
    this.createListForm = this.formBuilder.group({
      name: new FormControl('', [Validators.required, Validators.minLength(3)]),
      description: new FormControl('', [Validators.minLength(3)]),
      visibility: new FormControl('', [Validators.required])
    });
    this.createListForm.controls.visibility.setValue(this.visibilityOptions[0].value);
  }
  // todo: handle private/public list
  onCreateListClick() {
    this.waitingForResponse = true;
    this.userInteractionService
      .addList({ name: this.getName(), visible: this.getVisibility(), description: this.getDescription() })
      .pipe(
        map(lists => {
          if (lists.length === 0 || lists.filter(list => list.movies).length === 0) {
            throw new Error('List is duplicated or empty');
          }
          return lists;
        })
      )
      .subscribe(
        lists => {
          this.messageService.add({
            severity: 'success',
            summary: $localize`:@@ui-create-list-form.create.success.title:Success`,
            detail: $localize`:@@ui-create-list-form.create.success.description:List created`
          });

          this.waitingForResponse = false;
          this.listCreated.emit(lists.find(list => list.name === this.getName()));
        },
        error => {
          this.messageService.add({
            severity: 'error',
            summary: $localize`:@@ui-create-list-form.create.error.title:Error`,
            detail: $localize`:@@ui-create-list-form.create.error.description:List could not be created`
          });

          this.waitingForResponse = false;
          console.error(error);
        }
      );
  }
  getDescription() {
    return this.createListForm.controls.description.value;
  }
  getName() {
    return this.createListForm.controls.name.value;
  }
  getVisibility() {
    return this.createListForm.controls.visibility.value;
  }
}
